import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { ReferralProvider } from "@/context/ReferralContext";

import Layout from "@/components/layout/Layout";
import HomePage from "@/pages/HomePage";
import CatalogPage from "@/pages/CatalogPage";
import ProductPage from "@/pages/ProductPage";
import CartPage from "@/pages/CartPage";
import CheckoutPage from "@/pages/CheckoutPage";
import OrderSuccessPage from "@/pages/OrderSuccessPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import AccountPage from "@/pages/AccountPage";
import OrderLookupPage from "@/pages/OrderLookupPage";
import DealsPage from "@/pages/DealsPage";
import DealsUnder20Page from "@/pages/DealsUnder20Page";
import EbikeComparePage from "@/pages/EbikeComparePage";
import AdultPage from "@/pages/AdultPage";
import PalletSalesPage from "@/pages/PalletSalesPage";
import DepartmentsPage from "@/pages/DepartmentsPage";
import PalletListPage from "@/pages/PalletListPage";
import PalletBuilderPage from "@/pages/PalletBuilderPage";
import DeliveryPolicyPage from "@/pages/DeliveryPolicyPage";
import ReturnPolicyPage from "@/pages/ReturnPolicyPage";
import PalletPolicyPage from "@/pages/PalletPolicyPage";
import PrivacyPage from "@/pages/PrivacyPage";
import TermsPage from "@/pages/TermsPage";
import ResellersPage from "@/pages/ResellersPage";
import AboutPage from "@/pages/AboutPage";
import TobaccoPage from "@/pages/TobaccoPage";
import HalloweenPage from "@/pages/HalloweenPage";
import NotFoundPage from "@/pages/NotFoundPage";
import AdminSupplierOrders from "@/pages/admin/AdminSupplierOrders";
import AdminSupplierImport from "@/pages/admin/AdminSupplierImport";

import AdminLayout from "@/components/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminProducts from "@/pages/admin/AdminProducts";
import AdminQuickAdd from "@/pages/admin/AdminQuickAdd";
import AdminOrders from "@/pages/admin/AdminOrders";
import AdminDiscounts from "@/pages/admin/AdminDiscounts";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminCashiers from "@/pages/admin/AdminCashiers";
import AdminSalesReps from "@/pages/admin/AdminSalesReps";
import AdminStaff from "@/pages/admin/AdminStaff";
import AdminAudit from "@/pages/admin/AdminAudit";
import AdminTextToBuy from "@/pages/admin/AdminTextToBuy";
import SalesRepSignupPage from "@/pages/sales-rep/SalesRepSignupPage";
import SalesRepDashboardPage from "@/pages/sales-rep/SalesRepDashboardPage";
import { useEffect } from "react";
import POSPage from "@/pages/pos/POSPage";
import PosSquareCallbackPage from "@/pages/pos/PosSquareCallbackPage";
import PayLinkPage from "@/pages/PayLinkPage";
import AdminReports from "@/pages/admin/AdminReports";
import AdminHelp from "@/pages/admin/AdminHelp";
import DriverPage from "@/pages/driver/DriverPage";
import { useNavigate as useNav } from "react-router-dom";

function RequireAdmin({ children }) {
  const { user, loading } = useAuth();
  const navigate = useNav();
  // Effect-based redirect for cashier so the URL is always rewritten cleanly
  // (avoids a loading-state race where <Navigate> doesn't commit before
  // the wrapped admin layout begins rendering).
  useEffect(() => {
    if (!loading && (user?.role === "cashier" || user?.role === "associate")) {
      navigate("/pos", { replace: true });
    }
    if (!loading && user?.role === "sales_rep") {
      navigate("/sales-rep/dashboard", { replace: true });
    }
    if (!loading && user?.role === "driver") {
      navigate("/driver", { replace: true });
    }
  }, [loading, user, navigate]);
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "cashier" || user.role === "associate") return null; // useEffect above is taking us to /pos
  if (user.role === "sales_rep") return null; // useEffect above is taking us to /sales-rep/dashboard
  if (user.role === "driver") return null;
  if (user.role !== "admin" && user.role !== "owner") return <Navigate to="/login" replace />;
  return children;
}

function RequireStaff({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user || !["admin", "owner", "manager", "cashier", "associate"].includes(user.role)) {
    return <Navigate to="/login" replace />;
  }
  return children;
}

function RequireDriver({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user || !["driver", "admin", "owner", "manager"].includes(user.role)) {
    return <Navigate to="/login" replace />;
  }
  return children;
}

function RequireSalesRep({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "sales_rep") return <Navigate to="/" replace />;
  return children;
}

function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

// Every route change scrolls back to the top (SPA fix — user-reported bug)
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [pathname]);
  return null;
}

function App() {
  return (
    <AuthProvider>
      <CartProvider>
        <BrowserRouter>
          <ReferralProvider>
            <ScrollToTop />
            <Toaster position="top-right" richColors />
            <Routes>
              <Route element={<Layout />}>
                <Route path="/" element={<HomePage />} />
                <Route path="/shop" element={<CatalogPage />} />
                <Route path="/departments" element={<DepartmentsPage />} />
                <Route path="/e-bikes" element={<Navigate to="/shop/E-Bikes" replace />} />
                <Route path="/build-your-own-pallet" element={<Navigate to="/build-pallet" replace />} />
                <Route path="/deals" element={<DealsPage />} />
                <Route path="/deals-under-20" element={<DealsUnder20Page />} />
                <Route path="/halloween" element={<HalloweenPage />} />
                <Route path="/ebikes/compare" element={<EbikeComparePage />} />
                <Route path="/adult" element={<AdultPage />} />
                <Route path="/policies/delivery" element={<DeliveryPolicyPage />} />
                <Route path="/policies/returns" element={<ReturnPolicyPage />} />
                <Route path="/policies/pallets" element={<PalletPolicyPage />} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/resellers" element={<ResellersPage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/tobacco" element={<TobaccoPage />} />
                <Route path="/build-pallet" element={<PalletBuilderPage />} />
                <Route path="/pallets" element={<PalletSalesPage />} />
                <Route path="/pallets/:type" element={<PalletListPage />} />
                <Route path="/shop/:category" element={<CatalogPage />} />
                <Route path="/product/:slug" element={<ProductPage />} />
                <Route path="/cart" element={<CartPage />} />
                <Route path="/checkout" element={<CheckoutPage />} />
                <Route path="/order/:orderNumber" element={<OrderSuccessPage />} />
                <Route path="/track" element={<OrderLookupPage />} />
                <Route path="/track-order" element={<Navigate to="/track" replace />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/sales-rep/signup" element={<SalesRepSignupPage />} />
                <Route
                  path="/sales-rep/dashboard"
                  element={
                    <RequireSalesRep>
                      <SalesRepDashboardPage />
                    </RequireSalesRep>
                  }
                />
                <Route
                  path="/account"
                  element={
                    <RequireAuth>
                      <AccountPage />
                    </RequireAuth>
                  }
                />
              </Route>
              <Route
                path="/admin"
                element={
                  <RequireAdmin>
                    <AdminLayout />
                  </RequireAdmin>
                }
              >
                <Route index element={<AdminDashboard />} />
                <Route path="products" element={<AdminProducts />} />
                <Route path="quick-add" element={<AdminQuickAdd />} />
                <Route path="orders" element={<AdminOrders />} />
            <Route path="supplier-orders" element={<AdminSupplierOrders />} />
            <Route path="supplier-import" element={<AdminSupplierImport />} />
                <Route path="discounts" element={<AdminDiscounts />} />
                <Route path="text-to-buy" element={<AdminTextToBuy />} />
                <Route path="cashiers" element={<AdminCashiers />} />
                <Route path="sales-reps" element={<AdminSalesReps />} />
                <Route path="staff" element={<AdminStaff />} />
                <Route path="audit" element={<AdminAudit />} />
                <Route path="reports" element={<AdminReports />} />
                <Route path="help" element={<AdminHelp />} />
                <Route path="settings" element={<AdminSettings />} />
              </Route>
              <Route path="/pay/:token" element={<PayLinkPage />} />
              <Route
                path="/driver"
                element={
                  <RequireDriver>
                    <DriverPage />
                  </RequireDriver>
                }
              />
              <Route
                path="/pos"
                element={
                  <RequireStaff>
                    <POSPage />
                  </RequireStaff>
                }
              />
              <Route
                path="/pos/square-callback"
                element={
                  <RequireStaff>
                    <PosSquareCallbackPage />
                  </RequireStaff>
                }
              />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </ReferralProvider>
        </BrowserRouter>
      </CartProvider>
    </AuthProvider>
  );
}

export default App;
